/**
 * AssistantChatProvider — single Chat instance shared across all assistant
 * UI shells (AssistantOverlay, AssistantSidebar, AssistantFullTab).
 *
 * Why a provider: Vercel SDK v6's useChat constructs a NEW Chat instance per
 * call site, even when the `id` matches another mount. The id is a label, not
 * a registry key (see node_modules/@ai-sdk/react/src/use-chat.ts:98-100). So
 * three sibling shells mounting useChat({ id: 'workspace-chat' }) ended up
 * with three isolated message arrays — switching between them appeared to
 * "clear" the chat.
 *
 * Fix follows the official Vercel cookbook recipe
 * (https://ai-sdk.dev/cookbook/next/use-shared-chat-context): hold a single
 * Chat in context, have consumers call useChat({ chat }) with the shared
 * instance. This file owns:
 *   - The Chat instance (kept in useState; survives consumer mount/unmount)
 *   - The AssistantTransport (created alongside the Chat; same lifetime)
 *   - The getContext callback bound to the live ambient state — readable via
 *     a ref so the transport always sends the *current* focused entity / model
 *     even if the consumer that fired sendMessage isn't the focused panel
 *
 * Consumers use useAssistantChat() (no args) to access the curated surface.
 */

import {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  type ReactNode,
} from 'react';
import * as Sentry from '@sentry/react';
import { Chat } from '@ai-sdk/react';
import type { UIMessage } from 'ai';
import { useSyncTransport } from '@/lib/sync/sync-system-context';
import { useActiveEntityId } from '@/hooks/navigation';
import { useAssistantStore } from '@/stores/use-assistant-store';
import { useEntityStore } from '@/lib/sync/entity-store';
import {
  AssistantTransport,
  type AssistantTransportContext,
} from '../utils/assistant-transport';
import { useConversationRegistryStore } from '../stores/use-conversation-registry-store';
import { useAssistantNotificationsStore } from '../stores/use-assistant-notifications-store';
import { fetchConversationMessages } from '../api/conversations';
import { ApiError } from '@/lib/http';
import {
  startReconnectCoordinator,
  buildReconnectDeps,
} from '../utils/reconnect-coordinator';
import {
  UUID_RE,
  applyBackgroundNotification,
  applyConversationDoneBadge,
  applyApprovalBadge,
} from '../utils/background-notification';

// Re-export for consumers that import these helpers from this module
// (existing tests and any other callers keep resolving without changes).
export { applyBackgroundNotification, applyConversationDoneBadge, applyApprovalBadge };

/**
 * Handle an `emit_conversation_upserted` entity-sync event as a REDUNDANT
 * completion trigger (design §4). A run that completes while the WS stream was
 * disrupted never delivers a live DONE frame to the transport. The UPSERTED
 * event fires on reconnect when nexus fans out the persisted conversation update,
 * giving a second chance to surface the done badge.
 *
 * Fires `applyBackgroundNotification('done', …)` only if:
 *   - the conversationId is a real UUID (non-UUID overlay constants are skipped)
 *   - the conversation is NOT currently focused (focused → inline, no badge)
 *   - the connection-scoped registry still marks the conversation `running`
 *     (if already `done` or `idle`, the badge was already surfaced or no run ran)
 *
 * Exported for direct unit test; called from the provider's entity-store
 * subscription.
 */
export function applyUpsertedBadge(
  conversationId: string,
  focusedId: string | null,
): void {
  if (!UUID_RE.test(conversationId)) return;
  if (conversationId === focusedId) return;
  const registryState = useConversationRegistryStore.getState();
  if (registryState.statuses[conversationId] !== 'running') return;
  useAssistantNotificationsStore.getState().markDone(conversationId);
}

/**
 * The {chat, transport} bundle the connection-scoped registry holds per chatId.
 * Shared across all providers/surfaces that mount with that id: two providers
 * mounting with the same chatId expose the SAME Chat AND the SAME transport. The
 * bundle's dispose() tears the transport down when the registry evicts it.
 *
 * Both chat AND transport must be registry-owned (not per-provider): before this
 * a per-provider useMemo transport leaked the second provider's transport (T2) and
 * routed loadConversation/startNewConversation run-map writes to the wrong transport.
 */
interface ChatBundle {
  chat: Chat<UIMessage>;
  transport: AssistantTransport;
  /** Delegates to chat.messages — documented contract for registry consumers. */
  get messages(): UIMessage[];
  dispose: () => void;
}

interface AssistantChatContextValue {
  /** The shared Chat instance — passed into useChat({ chat }) by useAssistantChat. */
  chat: Chat<UIMessage>;
  /** The shared transport — exposed so the hook can wire stop()/runId/sendApprovalResponse. */
  transport: AssistantTransport;
  /** Stable chatId used by the transport's runByChatId map. */
  chatId: string;
  /**
   * Load a persisted conversation into the chat view.
   * Fetches the UIMessage[] blob from nexus, hydrates the Chat, and presets
   * the transport so the next user message continues the same thread (resumeRunId
   * cold-path). Sets currentConversationId in the assistant store.
   */
  loadConversation: (conversationId: string) => Promise<void>;
  /**
   * Reset to a fresh chat — clears the Chat messages, removes the transport's
   * preset runId, and clears currentConversationId. The next user message
   * starts a new run.
   */
  startNewConversation: () => void;
}

const AssistantChatContext = createContext<AssistantChatContextValue | null>(null);

export interface AssistantChatProviderProps {
  children: ReactNode;
  /**
   * Stable chat id == conversationId == corrId. Minted at tab creation (§7.4).
   * Required: the provider is keyed by its real conversationId from the first
   * render — no 'scratch' placeholder, no promotion.
   */
  chatId: string;
  /**
   * Resolves the per-message context (active entity, model, pinned items)
   * sent over the wire with every user message.
   *
   * The provider stores this in a ref so the transport always reads the
   * *current* function on each sendMessages call — callers can re-render
   * with a new closure (e.g. focused-panel change) without recreating the
   * Chat or transport.
   *
   * Defaults to a minimal stub (`activeEntityId: null, model: 'chat'`) so
   * tests can render the provider without wiring zustand. Production
   * mounts pass the real getContext that reads from the ambient stores.
   */
  getContext?: () => AssistantTransportContext;
  /** Optional turn-end callback (forwarded to the transport). */
  onTurnComplete?: (stats: {
    iterations: number;
    toolsCalled: number;
    hasPendingApproval: boolean;
  }) => void;
  /** Optional conversation-end callback (forwarded to the transport). */
  onConversationDone?: (stats: {
    iterations: number;
    toolsCalled: number;
    cancelled: boolean;
  }) => void;
  /**
   * Optional first-send callback (forwarded alongside the built-in setCurrent).
   * Fires once when this chat sends its first message — used by the ambient
   * provider to promote its minted UUID into the ambient conversation pointer.
   */
  onConversationStarted?: (conversationId: string) => void;
}

const defaultGetContext = (): AssistantTransportContext => ({
  activeEntityId: null,
  model: 'chat',
});

export function AssistantChatProvider({
  children,
  chatId,
  getContext,
  onTurnComplete,
  onConversationDone,
  onConversationStarted,
}: AssistantChatProviderProps) {
  const syncTransport = useSyncTransport();

  // Initialize the connection-scoped registry store once the sync transport is
  // available. The store uses a provider refcount: init() increments on each
  // mount and subscribes the firehose only on 0→1 (first provider); teardown()
  // decrements and detaches only on 1→0 (last provider unmounts). This keeps
  // liveness running while at least one provider is mounted — the workspace
  // overlay provider stays mounted when a conversation tab unmounts, so a single
  // tab teardown must NOT detach the consumer (§8.4). A different syncTransport
  // (workspace switch) re-subscribes immediately regardless of count.
  useEffect(() => {
    useConversationRegistryStore.getState().init(syncTransport);
    return () => {
      useConversationRegistryStore.getState().teardown();
    };
  }, [syncTransport]);

  // Reconnect-mid-run reconciliation. On a disconnected→connected edge, for each
  // conversation the registry (single liveness owner) marks running, refetch the
  // persisted transcript, reconcile it with the live Chat messages, resume the
  // firehose if the run is still live, and apply the merged list back to the
  // warm Chat. Deps resolve per-chatId against the registry so each run resumes
  // on its OWN transport (buildReconnectDeps). Tied to the syncTransport lifetime
  // — recreated on a workspace switch, disposed on unmount.
  useEffect(() => {
    return startReconnectCoordinator(
      buildReconnectDeps({ syncTransport, fetchMessages: fetchConversationMessages }),
    );
  }, [syncTransport]);

  // Keep the latest getContext / callbacks in refs so re-rendering with a new
  // closure does NOT recreate the Chat or transport. Without refs we'd either
  // capture stale data or thrash the Chat instance, defeating the whole point
  // of the provider.
  const getContextRef = useRef<() => AssistantTransportContext>(
    getContext ?? defaultGetContext,
  );
  const onTurnCompleteRef = useRef(onTurnComplete);
  const onConversationDoneRef = useRef(onConversationDone);
  const onConversationStartedRef = useRef(onConversationStarted);

  useEffect(() => {
    getContextRef.current = getContext ?? defaultGetContext;
  }, [getContext]);

  useEffect(() => {
    onTurnCompleteRef.current = onTurnComplete;
  }, [onTurnComplete]);

  useEffect(() => {
    onConversationDoneRef.current = onConversationDone;
  }, [onConversationDone]);

  useEffect(() => {
    onConversationStartedRef.current = onConversationStarted;
  }, [onConversationStarted]);

  // UPSERTED-driven completion badge (design §4 — redundant trigger). A run that
  // completes while the WS stream was disrupted never delivers a live DONE frame.
  // The `emit_conversation_upserted` entity-sync event fires on reconnect when nexus
  // fans out the persisted conversation update. Subscribe to the entity store's
  // `conversations` Map; whenever an entry changes, check if the registry still
  // thinks that conversation is `running` — if so, surface the done badge.
  useEffect(() => {
    return useEntityStore.subscribe((state, prevState) => {
      const conversations = state.conversations;
      const prev = prevState.conversations;
      if (conversations === prev) return;
      const focusedId = useConversationRegistryStore.getState().focusedConversationId;
      for (const [id] of conversations) {
        if (!prev.has(id) || prev.get(id) !== conversations.get(id)) {
          applyUpsertedBadge(id, focusedId);
        }
      }
    });
  }, []);

  // Acquire the shared {chat, transport} bundle for this chatId from the registry.
  // Two providers mounted with the same chatId (e.g. the conversation open in a tab
  // AND the sidebar) share ONE Chat AND ONE transport — switching surfaces never
  // clones state or creates a second transport that could leak / write to the wrong
  // run-map.
  //
  // The factory builds BOTH the transport and the Chat so the registry owns them as
  // a unit. `syncTransport` and the ref-indirected callbacks are captured in the
  // factory closure; they don't need to be deps of any outer memo because the
  // registry only calls the factory ONCE per chatId (on first acquire). The ref
  // indirection means the transport always reads the *current* getContext / callbacks
  // even though the transport itself is never rebuilt across rerenders.
  //
  // NOTE: the registry's acquire is in useState (not useEffect) so the entry is
  // synchronously available for the initial render. StrictMode fires the useEffect
  // cleanup on dev-unmount → release → refCount 0 → dispose(); the remount re-runs
  // useEffect but NOT useState. This is a dev-only scenario (production main.tsx has
  // no StrictMode). dispose() is idempotent (see AssistantTransport) so the double-call
  // is safe; the disposed transport is served post-remount only in dev.
  const acquireBundle = (id: string): ChatBundle =>
    useConversationRegistryStore.getState().acquireChat(id, () => {
      const t = new AssistantTransport({
        syncTransport,
        getContext: () => getContextRef.current(),
        onTurnComplete: (stats) => onTurnCompleteRef.current?.(stats),
        onConversationDone: (stats) => {
          onConversationDoneRef.current?.(stats);
          applyConversationDoneBadge(id);
        },
        onApprovalRequested: (chatId) => {
          applyApprovalBadge(chatId);
        },
        onConversationStarted: (conversationId) => {
          useConversationRegistryStore.getState().setCurrent(conversationId);
          onConversationStartedRef.current?.(conversationId);
        },
      });
      // chatId IS the conversationId (minted at tab creation, §7.4). Preset so
      // the first send's corrId equals the chatId — one key, all layers.
      // Only preset when chatId is a real UUID — non-UUID ids (e.g. the
      // workspace overlay's stable constant) must take the normal first-turn
      // path so sendMessages mints a fresh runId instead of passing the
      // non-UUID string to uuidToBytes (which would throw "invalid UUID").
      if (UUID_RE.test(id)) t.presetRun(id, id);
      const c = new Chat<UIMessage>({ id, transport: t });
      return {
        chat: c,
        transport: t,
        // Getter so consumers can read messages without reaching into bundle.chat.
        get messages() { return c.messages; },
        // The registry disposes this bundle on eviction; tear down the transport.
        dispose: () => t.dispose(),
      };
    }) as ChatBundle;

  // chatId is fixed for the lifetime of this provider (the tab is keyed by its
  // real conversationId from creation — §7.4). A different conversation mounts a
  // DIFFERENT provider with a DIFFERENT chatId, so no in-place re-acquire is needed.
  const [bundle] = useState(() => acquireBundle(chatId));
  const { chat, transport } = bundle;

  // Release on unmount (or chatId change). The registry keeps a running or warm
  // Chat alive at refCount 0 — a background run keeps draining the firehose, and
  // a recently-done conversation stays warm for switch-back. The transport is
  // only disposed when the registry actually evicts the entry (grace timer/LRU),
  // never on a plain tab unmount. A pending tool approval therefore survives the
  // unmount: nothing here sends an approval-cancel (design §8.4).
  useEffect(() => {
    return () => useConversationRegistryStore.getState().releaseChat(chatId);
  }, [chatId]);

  /**
   * Load a persisted conversation's UIMessage[] blob into the Chat and preset
   * the transport so the next RUN binds chatId → conversationId (Variant B §7.3).
   * The next send carries NO history body — nexus fills StartRun.history from
   * conversation_messages rows at RUN time.
   *
   * The messages setter is `AbstractChat.messages =` — a standard JS setter on
   * the abstract base class (verified in @ai-sdk/react dist/index.d.ts:
   * `set messages(messages: UI_MESSAGE[])`). It mutates the Chat's internal
   * state array directly, which triggers registered message callbacks so
   * consumers calling useChat({ chat }) see the updated messages.
   */
  const loadConversation = useCallback(async (conversationId: string) => {
    const store = useConversationRegistryStore.getState();
    const warm = store.getWarmChat(conversationId) as ChatBundle | null;
    // Switch-back warm-path: skip the fetch only when THIS provider's chatId is
    // the conversationId (ConversationTab) AND the warm Chat actually holds a
    // transcript. A ConversationTab's chatId IS its conversationId, so the entry
    // returned by getWarmChat is always the tab's own (just-acquired) bundle —
    // non-null even on a cold acquire or after the registry evicted the prior
    // Chat and re-warmed it with a fresh EMPTY one. Keying the warm path on the
    // entry merely existing therefore skipped the fetch for an empty Chat,
    // leaving the tab on the default "What's on your mind" page after a switch
    // away/back that evicted the warm Chat. Require a populated transcript so an
    // empty Chat falls through to the cold-open fetch below.
    if (warm && chatId === conversationId && warm.chat.messages.length > 0) {
      // Switch-back to a still-warm conversation: render from the kept-alive
      // Chat — NO refetch (design §7.2 step 2 / §8.3). The warm Chat already
      // holds the live transcript; just re-point the active transport and mark
      // this the current conversation.
      transport.presetRun(chatId, conversationId);
      store.setCurrent(conversationId);
      // Focusing a conversation clears any background badge it accrued.
      useAssistantNotificationsStore.getState().clear(conversationId);
      return;
    }
    // Cold open: hydrate from persisted rows.
    let messages: UIMessage[];
    try {
      messages = await fetchConversationMessages(conversationId);
    } catch (err) {
      // A 404 means there are no persisted messages yet for this id. The common
      // case is a BRAND-NEW conversation tab loading its own id before the first
      // turn is persisted (the rarer case is a deleted/purged conversation).
      // Either way reset to an empty chat — but KEEP the run bound to chatId so
      // the first send runs under it. ConversationTab always loads its OWN
      // chatId, so re-presetting (chatId === conversationId) upholds the
      // cross-layer invariant (chatId === conversationId === runId). Clearing the
      // binding here was the bug: the first send then minted a fresh random
      // runId, the conversation was persisted + auto-titled under THAT id, and
      // the tab (keyed by chatId) never saw the title. The caller (`void
      // loadConversation(...)` in ConversationTab) doesn't catch, so re-throwing
      // would surface as an unhandled rejection; and per CLAUDE.md an expected
      // condition must NOT be captured to Sentry.
      if (err instanceof ApiError && err.status === 404) {
        chat.messages = [];
        transport.presetRun(chatId, conversationId);
        store.setCurrent(null);
        return;
      }
      // Genuine failures (500, network) remain errors worth surfacing.
      Sentry.captureException(err, { extra: { conversationId } });
      throw err;
    }
    // Hydrate the Chat with the persisted message history.
    // API: AbstractChat.set messages(UIMessage[]) — JS property setter on
    // the base class (not setMessages on UseChatHelpers, which is a useChat
    // return value, not a Chat method).
    chat.messages = messages;
    // Preset the transport to resume this conversation on the next send.
    transport.presetRun(chatId, conversationId);
    store.setCurrent(conversationId);
    // Focusing a conversation clears any background badge it accrued.
    useAssistantNotificationsStore.getState().clear(conversationId);
  }, [chat, chatId, transport]);

  /**
   * Reset to a fresh unbound chat. Clears the Chat messages, removes the
   * transport's preset runId (so the next send starts a new run), and
   * clears currentConversationId.
   */
  const startNewConversation = useCallback(() => {
    chat.messages = [];
    transport.clearRunId(chatId);
    useConversationRegistryStore.getState().setCurrent(null);
  }, [chat, chatId, transport]);

  const value = useMemo<AssistantChatContextValue>(
    () => ({ chat, transport, chatId, loadConversation, startNewConversation }),
    [chat, transport, chatId, loadConversation, startNewConversation],
  );

  return (
    <AssistantChatContext.Provider value={value}>
      {children}
    </AssistantChatContext.Provider>
  );
}

/**
 * Internal accessor: returns the raw context value or throws if no provider.
 * Exposed for useAssistantChat (which wraps useChat with the shared instance)
 * — callers should normally use useAssistantChat directly.
 */
export function useAssistantChatContext(): AssistantChatContextValue {
  const ctx = useContext(AssistantChatContext);
  if (ctx === null) {
    throw new Error(
      'useAssistantChat must be used within an AssistantChatProvider',
    );
  }
  return ctx;
}

/**
 * Hook that builds the real AssistantTransportContext from the live ambient
 * state — focused panel's active entity (via useActiveEntityId), selected
 * model + pinned context docs (from useAssistantStore), and entity metadata
 * (from useEntityStore).
 *
 * Used by both WorkspaceAssistantChatProvider (workspace overlay chat) and
 * ConversationTab (per-tab chats), so model selection and pinned-context
 * forwarding work consistently across all assistant surfaces.
 */
export function useWorkspaceGetContext(): () => AssistantTransportContext {
  // Subscribe to the focused panel's active entity. The hook only triggers a
  // re-render when it actually changes; getContextRef inside the inner provider
  // then carries the latest value into every transport.sendMessages call.
  const activeEntityId = useActiveEntityId();

  return useCallback((): AssistantTransportContext => {
    const { selectedModel, contextDocIds } = useAssistantStore.getState();
    let activeEntityTitle: string | null = null;
    let activeEntityKind: 'document' | 'canvas' = 'document';
    if (activeEntityId) {
      const { entityMeta, canvases } = useEntityStore.getState();
      if (entityMeta.has(activeEntityId)) {
        activeEntityTitle = entityMeta.get(activeEntityId)?.title ?? null;
        activeEntityKind = 'document';
      } else if (canvases.has(activeEntityId)) {
        activeEntityTitle = canvases.get(activeEntityId)?.title ?? null;
        activeEntityKind = 'canvas';
      }
    }
    // Pinned docs come from useAssistantStore.contextDocIds — resolve to
    // title via entityMeta. We deliberately do not call useWorkspaceDocs here
    // because that hook subscribes to a large slice; we only need title lookup
    // and entityMeta has it. Falls back to the id when no title is loaded.
    const { entityMeta } = useEntityStore.getState();
    const pinnedItems = contextDocIds.map((id) => ({
      type: 'document' as const,
      id,
      title: entityMeta.get(id)?.title ?? id,
      relevance: 'pinned' as const,
    }));
    return {
      activeEntityId,
      activeEntityTitle,
      activeEntities: activeEntityId
        ? [
            {
              id: activeEntityId,
              type: activeEntityKind,
              relevance: 'focused' as const,
              title: activeEntityTitle ?? undefined,
            },
          ]
        : undefined,
      contextItems: pinnedItems.length > 0 ? pinnedItems : undefined,
      model: selectedModel,
    };
  }, [activeEntityId]);
}

/**
 * Production wrapper: AssistantChatProvider with a getContext that reads
 * the live ambient state — focused panel's active entity (via useActiveEntityId),
 * selected model + pinned context docs (from useAssistantStore), and entity
 * metadata (from useEntityStore).
 *
 * The Overlay/Sidebar/FullTab shells used to build this body themselves with
 * a closure over their local activeEntityId. After the provider refactor the
 * provider owns the context computation — three shells now share the same
 * Chat instance and the same per-message context derivation.
 */

export function WorkspaceAssistantChatProvider({
  children,
}: {
  children: ReactNode;
}) {
  const getContext = useWorkspaceGetContext();

  // The ambient assistant (overlay + sidebar) is keyed by a real conversation
  // UUID from mount — NOT the legacy 'workspace-assistant' constant — so its
  // Chat/transport carry the conversation identity from birth. Expand-to-tab and
  // the sidebar then render the SAME live bundle by UUID (zero-copy hand-off).
  //
  // ambientConversationId (registry) is the promoted "real conversation" pointer,
  // null until first send. When it holds a UUID we render THAT (resume / shared);
  // otherwise we render a freshly-minted session UUID. Keying the provider on the
  // resolved id remounts it on reset (new conversation) — re-attaching to the
  // registry bundle for that id.
  const ambientConversationId = useConversationRegistryStore((s) => s.ambientConversationId);
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());
  const chatId = ambientConversationId ?? sessionId;

  // Promote the session UUID to the ambient conversation pointer on first send,
  // so the sidebar + expand-to-tab can find this conversation by id. A fresh
  // session (ambientConversationId null) mints sessionId; once promoted, reset
  // happens by clearing ambientConversationId + rotating sessionId.
  const handleFirstSend = useCallback(() => {
    const { ambientConversationId: current, setAmbientConversationId } =
      useConversationRegistryStore.getState();
    if (current == null) setAmbientConversationId(chatId);
  }, [chatId]);

  // When the ambient pointer clears (conversation moved to a tab / new chat),
  // rotate the session UUID so the overlay renders a FRESH chat — not re-attach
  // to the just-cleared conversation's bundle (the old sessionId was promoted).
  const prevAmbientRef = useRef(ambientConversationId);
  useEffect(() => {
    if (prevAmbientRef.current != null && ambientConversationId == null) {
      setSessionId(crypto.randomUUID());
    }
    prevAmbientRef.current = ambientConversationId;
  }, [ambientConversationId]);

  return (
    <AssistantChatProvider
      key={chatId}
      chatId={chatId}
      getContext={getContext}
      onConversationStarted={handleFirstSend}
    >
      {children}
    </AssistantChatProvider>
  );
}
